import type {
  ConnectorId,
  ConnectorSnapshot,
  MediaAnalysis,
  MediaClaim,
  MediaItem,
  TranscriptMediaSourceId,
} from "./connectors/types";
import type { ProjectionSet } from "./projections";
import { simulationModels, type SimulationRun } from "./simulations";
import { systems, type SystemId } from "./systems";
import type { TimelineEvidenceEvent } from "./timeline-types";
import { transcriptMediaSourceIds } from "./connectors/transcript-sources";
import type {
  SignalSourceNode,
  SignalSourceObservation,
  SignalSourceRegistry,
} from "./signal-source-registry";
import type { SourceAuthorityGraph } from "./source-authority";

export const signalGraphSourceIds: readonly TranscriptMediaSourceId[] = transcriptMediaSourceIds;

export type SignalGraphSourceId = TranscriptMediaSourceId;
export type SignalProcessingStatus =
  | "discovered"
  | "awaiting_transcript"
  | "analyzing"
  | "ready"
  | "failed";

export type SignalEpisode = {
  id: string;
  sourceId: SignalGraphSourceId;
  sourceName: string;
  videoId: string;
  title: string;
  url: string;
  publishedAt: string;
  mediaType?: MediaItem["type"];
  durationSeconds: number | null;
  transcriptAvailable: boolean;
  discoveredAt: string;
};

export type SignalMarketCandidate = {
  platform: "kalshi" | "polymarket";
  marketId: string;
  title: string;
  subtitle: string;
  url: string;
  status: string;
  closesAt: string | null;
  observedAt: string;
  probability: number | null;
  bid: number | null;
  ask: number | null;
  query: string;
};

export type SignalEntityLink = {
  entityId: string;
  label: string;
  connectorId: ConnectorId | null;
  claimIds: string[];
  confidence: "high" | "medium" | "low";
  rationale: string;
};

export type SignalSystemLink = {
  systemId: SystemId;
  name: string;
  claimIds: string[];
  confidence: "high" | "medium" | "low";
  rationale: string;
};

export type SignalMarketLink = SignalMarketCandidate & {
  claimIds: string[];
  relevanceScore: number;
  confidence: "high" | "medium" | "low";
  rationale: string;
};

export type SignalSimulationLink = {
  modelId: string;
  runId: string | null;
  modelConfidence: number | null;
  conditioning: "direct_source" | "linked_context";
  systemIds: SystemId[];
  claimIds: string[];
  rationale: string;
};

export type SignalProjectionLink = {
  projectionId: string;
  simulationModelId: string;
  modelConfidence: number;
  claimIds: string[];
  rationale: string;
};

export type SignalGraphNode = {
  id: string;
  kind:
    | "source"
    | "episode"
    | "claim"
    | "evidence"
    | "entity"
    | "system"
    | "market"
    | "simulation"
    | "projection";
  label: string;
  observedAt: string;
  url?: string;
};

export type SignalGraphEdge = {
  id: string;
  from: string;
  to: string;
  relation:
    | "published"
    | "extracted_from"
    | "derived_from"
    | "supported_by"
    | "mentions"
    | "affects"
    | "mapped_to_market"
    | "conditions_simulation"
    | "updates_projection";
  confidence: "high" | "medium" | "low";
  rationale: string;
};

export type SignalProvenance = {
  id: string;
  kind: "media" | "transcript_evidence" | "market_snapshot" | "model_run";
  label: string;
  url?: string;
  observedAt: string;
  availableAt: string;
};

export type SignalPacket = {
  id: string;
  schemaVersion: 1;
  revision: number;
  status: "ready";
  episode: SignalEpisode;
  analysis: MediaAnalysis;
  claims: MediaClaim[];
  entities: SignalEntityLink[];
  systems: SignalSystemLink[];
  markets: SignalMarketLink[];
  simulations: SignalSimulationLink[];
  projections: SignalProjectionLink[];
  nodes: SignalGraphNode[];
  edges: SignalGraphEdge[];
  provenance: SignalProvenance[];
  completedAt: string;
  limitations: string[];
};

export type SignalGraphEntry = {
  id: string;
  sourceId: SignalGraphSourceId;
  videoId: string;
  status: SignalProcessingStatus;
  episode: SignalEpisode;
  attempts: number;
  discoveredAt: string;
  updatedAt: string;
  retryAt: string | null;
  leaseId: string | null;
  leaseExpiresAt: string | null;
  lastError: string | null;
  packet: SignalPacket | null;
};

export type SignalGraphEntrySummary = Omit<SignalGraphEntry, "packet"> & {
  claimCount: number;
  marketCount: number;
  systemCount: number;
  simulationCount: number;
};

export type SignalGraphSummary = {
  checkedAt: string;
  trackedSourceCount: number;
  connectedSourceCount: number;
  liveSourceCount: number;
  registeredSourceCount: number;
  blockedSourceCount: number;
  transcriptSourceCount: number;
  observationCount: number;
  systemLinkCount: number;
  categoryCounts: Record<ConnectorSnapshot["category"], number>;
  total: number;
  discovered: number;
  processing: number;
  ready: number;
  failed: number;
  latestCompletedAt: string | null;
};

export type SignalGraphResponse = {
  summary: SignalGraphSummary;
  sources: SignalSourceNode[];
  sourceAuthority: SourceAuthorityGraph;
  observations: SignalSourceObservation[];
  timelineObservations: SignalSourceObservation[];
  signals: SignalGraphEntrySummary[];
  packets: SignalPacket[];
  execution: "read_only";
};

function normalize(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function words(value: string) {
  const stop = new Set([
    "a", "an", "and", "are", "as", "at", "be", "before", "by", "for", "from",
    "in", "is", "it", "of", "on", "or", "the", "to", "will", "with", "within",
  ]);
  return normalize(value).split(" ").filter((word) => word.length > 1 && !stop.has(word));
}

function hash(value: string) {
  let result = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16_777_619);
  }
  return (result >>> 0).toString(16).padStart(8, "0");
}

function confidence(score: number): "high" | "medium" | "low" {
  if (score >= 70) return "high";
  if (score >= 40) return "medium";
  return "low";
}

function unique<T>(values: T[]) {
  return Array.from(new Set(values));
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function signalPacketId(sourceId: SignalGraphSourceId, videoId: string) {
  return `signal-${sourceId}-${videoId.replace(/[^a-zA-Z0-9_-]+/g, "-")}`;
}

export function signalEpisodeFromMediaItem(
  sourceId: SignalGraphSourceId,
  sourceName: string,
  item: MediaItem,
  discoveredAt = new Date().toISOString(),
): SignalEpisode {
  if (!item.videoId) throw new Error("A Signal Graph episode requires a stable YouTube video id.");
  return {
    id: `episode-${sourceId}-${item.videoId}`,
    sourceId,
    sourceName,
    videoId: item.videoId,
    title: item.title,
    url: item.url,
    publishedAt: item.publishedAt,
    mediaType: item.type,
    durationSeconds: item.durationSeconds ?? null,
    transcriptAvailable: item.transcriptAvailable,
    discoveredAt,
  };
}

function namespacedClaims(packetId: string, analysis: MediaAnalysis) {
  const claimIds = new Map(analysis.claims.map((claim) => [claim.id, `${packetId}:claim:${claim.id}`]));
  return analysis.claims.map((claim) => ({
    ...clone(claim),
    id: claimIds.get(claim.id)!,
    parentClaimIds: claim.parentClaimIds.map((id) => claimIds.get(id) ?? `${packetId}:claim:${id}`),
    evidence: claim.evidence.map((evidence) => ({
      ...evidence,
      id: `${packetId}:evidence:${evidence.id}`,
    })),
  }));
}

function connectorForEntity(connectors: ConnectorSnapshot[], entity: string) {
  const candidate = normalize(entity);
  if (!candidate) return null;
  return connectors.find((connector) => {
    const id = normalize(connector.id);
    const name = normalize(connector.name);
    return candidate === id
      || candidate === name
      || (candidate.length >= 5 && (name.includes(candidate) || candidate.includes(name)));
  }) ?? null;
}

function buildEntityLinks(
  claims: MediaClaim[],
  connectors: ConnectorSnapshot[],
) {
  const grouped = new Map<string, { label: string; connectorId: ConnectorId | null; claimIds: string[] }>();
  claims.forEach((claim) => {
    const candidates = [
      ...claim.entities,
      ...(claim.sourceAssessment.personId ? [claim.speaker.name] : []),
    ];
    candidates.forEach((label) => {
      const connector = claim.sourceAssessment.personId
        ? connectors.find((item) => item.id === claim.sourceAssessment.personId)
        : connectorForEntity(connectors, label);
      const key = connector?.id ?? `unresolved-${hash(normalize(label))}`;
      const current = grouped.get(key) ?? { label: connector?.name ?? label, connectorId: connector?.id ?? null, claimIds: [] };
      current.claimIds.push(claim.id);
      grouped.set(key, current);
    });
  });
  return Array.from(grouped.entries()).map(([entityId, value]) => ({
    entityId,
    label: value.label,
    connectorId: value.connectorId,
    claimIds: unique(value.claimIds),
    confidence: value.connectorId ? "high" as const : "low" as const,
    rationale: value.connectorId
      ? "The claim entity resolves to a registered Asymmetric source record."
      : "The entity is retained as unresolved rather than forced onto a registered source.",
  }));
}

const systemKeywords: Partial<Record<SystemId, RegExp>> = {
  financial: /\b(?:ipo|market|invest|fund|capital|stock|bond|credit|liquidity|valuation|trade|price|financ)\b/i,
  "ai-development-deployment": /\b(?:ai|artificial intelligence|openai|anthropic|model|compute|chip|inference|training)\b/i,
  legal: /\b(?:law|legal|court|regulat|sec|cftc|antitrust|filing|compliance)\b/i,
  "security-defense": /\b(?:security|military|defen[cs]e|weapon|war|intelligence|cyber)\b/i,
  "united-states": /\b(?:united states|u\.?s\.?|america|washington|federal)\b/i,
  china: /\b(?:china|chinese|beijing)\b/i,
  russia: /\b(?:russia|russian|moscow)\b/i,
  europe: /\b(?:europe|european|eu\b|brussels)\b/i,
  "strategic-interaction": /\b(?:geopolit|sanction|export control|alliance|deterrence|strategic competition)\b/i,
};

function buildSystemLinks(
  episode: SignalEpisode,
  claims: MediaClaim[],
  entities: SignalEntityLink[],
) {
  const linkedSourceIds = new Set<ConnectorId>([
    episode.sourceId,
    ...entities.flatMap((entity) => entity.connectorId ? [entity.connectorId] : []),
  ]);
  return systems.flatMap((system) => {
    const directlyLinked = system.sourceLinks.some((source) => linkedSourceIds.has(source.id));
    const pattern = systemKeywords[system.id];
    const claimIds = claims
      .filter((claim) => directlyLinked || Boolean(pattern?.test(`${claim.text} ${claim.entities.join(" ")}`)))
      .map((claim) => claim.id);
    if (!claimIds.length) return [];
    return [{
      systemId: system.id,
      name: system.name,
      claimIds: unique(claimIds),
      confidence: directlyLinked ? "high" as const : "medium" as const,
      rationale: directlyLinked
        ? "A resolved source or entity is explicitly linked to this registered system model."
        : "Claim language matches a declared mechanism boundary for this system; the link is contextual, not causal proof.",
    }];
  });
}

function marketRelevance(claim: MediaClaim, market: SignalMarketCandidate) {
  const claimText = `${claim.text} ${claim.entities.join(" ")} ${claim.marketQuestions.join(" ")}`;
  const marketText = `${market.title} ${market.subtitle}`;
  const claimTokens = new Set(words(claimText));
  const marketTokens = new Set(words(marketText));
  const overlap = Array.from(claimTokens).filter((token) => marketTokens.has(token));
  let score = overlap.length * 12;
  if (claim.entities.some((entity) => normalize(marketText).includes(normalize(entity)))) score += 28;
  if (claim.marketQuestions.some((question) => {
    const questionTokens = words(question);
    return questionTokens.length > 1 && questionTokens.filter((token) => marketTokens.has(token)).length >= 2;
  })) score += 20;
  if (claim.review.status === "supported") score += 6;
  if (claim.review.status === "qualified") score += 3;
  return Math.min(100, score);
}

function buildMarketLinks(claims: MediaClaim[], candidates: SignalMarketCandidate[]) {
  return candidates.flatMap((candidate) => {
    const matches = claims
      .map((claim) => ({ claim, score: marketRelevance(claim, candidate) }))
      .filter((match) => match.score >= 20)
      .sort((left, right) => right.score - left.score);
    if (!matches.length) return [];
    const score = matches[0].score;
    return [{
      ...candidate,
      claimIds: unique(matches.slice(0, 6).map((match) => match.claim.id)),
      relevanceScore: score,
      confidence: confidence(score),
      rationale:
        "Deterministic entity and question overlap links this point-in-time market snapshot to the claim. It does not establish mispricing, causality, or profitable execution.",
    }];
  }).sort((left, right) => right.relevanceScore - left.relevanceScore || right.observedAt.localeCompare(left.observedAt));
}

function buildSimulationLinks(
  episode: SignalEpisode,
  claims: MediaClaim[],
  systemLinks: SignalSystemLink[],
  runs: SimulationRun[],
) {
  const affectedSystems = new Set(systemLinks.map((link) => link.systemId));
  return simulationModels.flatMap((model) => {
    const systemIds = model.systemIds.filter((systemId) => affectedSystems.has(systemId));
    const directSource = model.requiredSourceIds.includes(episode.sourceId);
    if (!systemIds.length && !directSource) return [];
    const claimIds = unique(systemLinks
      .filter((link) => model.systemIds.includes(link.systemId))
      .flatMap((link) => link.claimIds));
    const run = runs.find((candidate) => candidate.modelId === model.id);
    return [{
      modelId: model.id,
      runId: run?.id ?? null,
      modelConfidence: run?.modelConfidence ?? null,
      conditioning: directSource ? "direct_source" as const : "linked_context" as const,
      systemIds,
      claimIds: claimIds.length ? claimIds : claims.map((claim) => claim.id),
      rationale: directSource
        ? "The media source is a declared model input and the model was rerun at packet completion."
        : "The signal touches a modeled system, so the model was rerun and linked. Its numeric parameters remain unchanged until a validated conditioning rule is registered.",
    }];
  });
}

function buildProjectionLinks(
  simulations: SignalSimulationLink[],
  projectionSets: ProjectionSet[],
) {
  return projectionSets.flatMap((projection) => {
    const simulation = simulations.find((link) => link.modelId === projection.simulationModelId);
    if (!simulation) return [];
    return [{
      projectionId: projection.id,
      simulationModelId: projection.simulationModelId,
      modelConfidence: projection.modelConfidence,
      claimIds: simulation.claimIds,
      rationale:
        "This forecast set is generated from the linked simulation run. The graph preserves model confidence separately from trajectory probability.",
    }];
  });
}

function addNode(nodes: Map<string, SignalGraphNode>, node: SignalGraphNode) {
  if (!nodes.has(node.id)) nodes.set(node.id, node);
}

function addEdge(edges: Map<string, SignalGraphEdge>, edge: Omit<SignalGraphEdge, "id">) {
  const id = `edge-${hash(`${edge.from}|${edge.relation}|${edge.to}`)}`;
  if (!edges.has(id)) edges.set(id, { id, ...edge });
}

export function buildSignalPacket(input: {
  episode: SignalEpisode;
  analysis: MediaAnalysis;
  connectors: ConnectorSnapshot[];
  marketCandidates?: SignalMarketCandidate[];
  simulationRuns?: SimulationRun[];
  projectionSets?: ProjectionSet[];
  completedAt?: string;
  revision?: number;
}) {
  if (input.analysis.videoId !== input.episode.videoId) {
    throw new Error("Signal analysis does not match the discovered episode.");
  }
  const completedAt = input.completedAt ?? new Date().toISOString();
  const id = signalPacketId(input.episode.sourceId, input.episode.videoId);
  const claims = namespacedClaims(id, input.analysis);
  const entities = buildEntityLinks(claims, input.connectors);
  const systemLinks = buildSystemLinks(input.episode, claims, entities);
  const marketLinks = buildMarketLinks(claims, input.marketCandidates ?? []);
  const simulationLinks = buildSimulationLinks(
    input.episode,
    claims,
    systemLinks,
    input.simulationRuns ?? [],
  );
  const projectionLinks = buildProjectionLinks(simulationLinks, input.projectionSets ?? []);
  const nodes = new Map<string, SignalGraphNode>();
  const edges = new Map<string, SignalGraphEdge>();
  const sourceNode = `source:${input.episode.sourceId}`;
  const episodeNode = `episode:${input.episode.id}`;

  addNode(nodes, {
    id: sourceNode,
    kind: "source",
    label: input.episode.sourceName,
    observedAt: input.episode.discoveredAt,
  });
  addNode(nodes, {
    id: episodeNode,
    kind: "episode",
    label: input.episode.title,
    observedAt: input.episode.publishedAt,
    url: input.episode.url,
  });
  addEdge(edges, {
    from: sourceNode,
    to: episodeNode,
    relation: "published",
    confidence: "high",
    rationale: "The episode is identified by the registered source feed and stable video id.",
  });

  claims.forEach((claim) => {
    const claimNode = `claim:${claim.id}`;
    addNode(nodes, {
      id: claimNode,
      kind: "claim",
      label: claim.text,
      observedAt: input.episode.publishedAt,
      url: claim.sourceUrl,
    });
    addEdge(edges, {
      from: claimNode,
      to: episodeNode,
      relation: claim.kind === "derived" ? "derived_from" : "extracted_from",
      confidence: claim.review.confidence,
      rationale: claim.derivation,
    });
    claim.parentClaimIds.forEach((parentId) => addEdge(edges, {
      from: claimNode,
      to: `claim:${parentId}`,
      relation: "derived_from",
      confidence: claim.review.confidence,
      rationale: claim.derivation,
    }));
    claim.evidence.forEach((evidence) => {
      const evidenceNode = `evidence:${evidence.id}`;
      addNode(nodes, {
        id: evidenceNode,
        kind: "evidence",
        label: evidence.quote,
        observedAt: input.episode.publishedAt,
        url: evidence.sourceUrl,
      });
      addEdge(edges, {
        from: evidenceNode,
        to: claimNode,
        relation: "supported_by",
        confidence: claim.review.confidence,
        rationale: "Timestamped source evidence supports this claim record.",
      });
    });
  });

  entities.forEach((entity) => {
    const entityNode = `entity:${entity.entityId}`;
    addNode(nodes, {
      id: entityNode,
      kind: "entity",
      label: entity.label,
      observedAt: input.episode.publishedAt,
    });
    entity.claimIds.forEach((claimId) => addEdge(edges, {
      from: `claim:${claimId}`,
      to: entityNode,
      relation: "mentions",
      confidence: entity.confidence,
      rationale: entity.rationale,
    }));
  });

  systemLinks.forEach((system) => {
    const systemNode = `system:${system.systemId}`;
    addNode(nodes, {
      id: systemNode,
      kind: "system",
      label: system.name,
      observedAt: completedAt,
    });
    system.claimIds.forEach((claimId) => addEdge(edges, {
      from: `claim:${claimId}`,
      to: systemNode,
      relation: "affects",
      confidence: system.confidence,
      rationale: system.rationale,
    }));
  });

  marketLinks.forEach((market) => {
    const marketNode = `market:${market.platform}:${market.marketId}`;
    addNode(nodes, {
      id: marketNode,
      kind: "market",
      label: market.title,
      observedAt: market.observedAt,
      url: market.url,
    });
    market.claimIds.forEach((claimId) => addEdge(edges, {
      from: `claim:${claimId}`,
      to: marketNode,
      relation: "mapped_to_market",
      confidence: market.confidence,
      rationale: market.rationale,
    }));
  });

  simulationLinks.forEach((simulation) => {
    const simulationNode = `simulation:${simulation.modelId}`;
    addNode(nodes, {
      id: simulationNode,
      kind: "simulation",
      label: simulationModels.find((model) => model.id === simulation.modelId)?.name ?? simulation.modelId,
      observedAt: completedAt,
    });
    simulation.systemIds.forEach((systemId) => addEdge(edges, {
      from: `system:${systemId}`,
      to: simulationNode,
      relation: "conditions_simulation",
      confidence: simulation.conditioning === "direct_source" ? "high" : "medium",
      rationale: simulation.rationale,
    }));
  });

  projectionLinks.forEach((projection) => {
    const projectionNode = `projection:${projection.projectionId}`;
    addNode(nodes, {
      id: projectionNode,
      kind: "projection",
      label: projection.projectionId,
      observedAt: completedAt,
    });
    addEdge(edges, {
      from: `simulation:${projection.simulationModelId}`,
      to: projectionNode,
      relation: "updates_projection",
      confidence: projection.modelConfidence >= 40 ? "medium" : "low",
      rationale: projection.rationale,
    });
  });

  const provenance = new Map<string, SignalProvenance>();
  provenance.set(`media-${input.episode.videoId}`, {
    id: `media-${input.episode.videoId}`,
    kind: "media",
    label: input.episode.title,
    url: input.episode.url,
    observedAt: input.episode.publishedAt,
    availableAt: input.episode.discoveredAt,
  });
  claims.flatMap((claim) => claim.evidence).forEach((evidence) => provenance.set(evidence.id, {
    id: evidence.id,
    kind: "transcript_evidence",
    label: evidence.quote,
    url: evidence.sourceUrl,
    observedAt: input.episode.publishedAt,
    availableAt: input.analysis.transcript.analyzedAt,
  }));
  marketLinks.forEach((market) => provenance.set(`market-${market.platform}-${market.marketId}`, {
    id: `market-${market.platform}-${market.marketId}`,
    kind: "market_snapshot",
    label: market.title,
    url: market.url,
    observedAt: market.observedAt,
    availableAt: market.observedAt,
  }));
  simulationLinks.forEach((simulation) => {
    if (!simulation.runId) return;
    provenance.set(`run-${simulation.runId}`, {
      id: `run-${simulation.runId}`,
      kind: "model_run",
      label: simulation.modelId,
      observedAt: completedAt,
      availableAt: completedAt,
    });
  });

  const packet: SignalPacket = {
    id,
    schemaVersion: 1,
    revision: input.revision ?? 1,
    status: "ready",
    episode: clone(input.episode),
    analysis: clone(input.analysis),
    claims,
    entities,
    systems: systemLinks,
    markets: marketLinks,
    simulations: simulationLinks,
    projections: projectionLinks,
    nodes: Array.from(nodes.values()),
    edges: Array.from(edges.values()),
    provenance: Array.from(provenance.values()),
    completedAt,
    limitations: [
      "A graph link is an inspectable research relation, not proof of causality or possession of nonpublic information.",
      "Market observations are immutable point-in-time snapshots; current prices may differ.",
      "Linked simulations are rerun, but contextual claims do not alter numeric parameters until a validated conditioning rule is registered.",
      "Signal Graph and Agent Core remain read only and cannot place or size orders.",
    ],
  };
  return packet;
}

export function signalTimelineEvents(packets: SignalPacket[]): TimelineEvidenceEvent[] {
  return packets.flatMap((packet) => packet.claims.map((claim) => ({
    id: `claim:${claim.id}`,
    at: claim.timeline.endDate,
    kind: "Claim deadline",
    sourceIds: [packet.episode.sourceId],
    title: claim.text,
    detail: `${claim.timeline.kind} · ${claim.timeline.rationale}`,
    url: claim.sourceUrl,
  })));
}

export function summarizeSignalGraph(
  entries: SignalGraphEntry[],
  registry: SignalSourceRegistry,
  checkedAt = new Date().toISOString(),
): SignalGraphSummary {
  const latestCompletedAt = entries
    .map((entry) => entry.packet?.completedAt ?? null)
    .filter((value): value is string => Boolean(value))
    .sort((left, right) => right.localeCompare(left))[0] ?? null;
  return {
    checkedAt,
    trackedSourceCount: registry.sources.length,
    connectedSourceCount: registry.sources.length,
    liveSourceCount: registry.sources.filter((source) => source.status === "live").length,
    registeredSourceCount: registry.sources.filter((source) => source.status === "registered").length,
    blockedSourceCount: registry.sources.filter((source) =>
      source.status === "credentials_required" || source.status === "unavailable").length,
    transcriptSourceCount: registry.sources.filter((source) =>
      source.adapters.includes("transcript_pipeline")).length,
    observationCount: registry.observations.length,
    systemLinkCount: registry.sources.reduce((count, source) => count + source.systemIds.length, 0),
    categoryCounts: registry.sources.reduce<SignalGraphSummary["categoryCounts"]>((counts, source) => {
      counts[source.category] += 1;
      return counts;
    }, { market: 0, media: 0, person: 0, institution: 0 }),
    total: entries.length,
    discovered: entries.filter((entry) => entry.status === "discovered" || entry.status === "awaiting_transcript").length,
    processing: entries.filter((entry) => entry.status === "analyzing").length,
    ready: entries.filter((entry) => entry.status === "ready").length,
    failed: entries.filter((entry) => entry.status === "failed").length,
    latestCompletedAt,
  };
}

export function summarizeSignalEntry(entry: SignalGraphEntry): SignalGraphEntrySummary {
  const summary = Object.fromEntries(
    Object.entries(entry).filter(([key]) => key !== "packet"),
  ) as Omit<SignalGraphEntry, "packet">;
  return {
    ...summary,
    claimCount: entry.packet?.claims.length ?? 0,
    marketCount: entry.packet?.markets.length ?? 0,
    systemCount: entry.packet?.systems.length ?? 0,
    simulationCount: entry.packet?.simulations.length ?? 0,
  };
}
